import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Users, Phone, CreditCard, Settings, 
  TrendingUp, Clock, CheckCircle, XCircle 
} from 'lucide-react';

const SaaSPreview = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({ calls: 0, completed: 0, duration: 0 });

  // Animer les stats progressivement
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        calls: Math.min(prev.calls + 1, 47),
        completed: Math.min(prev.completed + 1, 42),
        duration: Math.min(prev.duration + 5, 127)
      }));
    }, 50);
    
    return () => clearInterval(interval);
  }, []);

  const tabs = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
    { id: 'agents', icon: Users, label: 'Agents' },
    { id: 'calls', icon: Phone, label: 'Appels' },
    { id: 'billing', icon: CreditCard, label: 'Facturation' }
  ];

  return (
    <div className="w-full max-w-md mx-auto bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
      {/* Header avec dots */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-800/50 border-b border-white/5">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
      </div>

      {/* Sidebar + Content */}
      <div className="flex h-[400px]">
        {/* Sidebar */}
        <div className="w-16 bg-gray-900/80 border-r border-white/5 flex flex-col items-center py-4 gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-2">
            <span className="text-xs font-bold text-white">CU</span>
          </div>
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                activeTab === tab.id 
                  ? 'bg-sky-500/20 text-sky-400' 
                  : 'text-gray-500 hover:text-gray-300'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <tab.icon className="w-5 h-5" />
            </motion.button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 p-4 overflow-y-auto">
          <AnimatePresence mode="wait">
            {activeTab === 'dashboard' && (
              <motion.div
                key="dashboard"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-sm font-bold text-white mb-4">Tableau de bord</h3>
                
                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <Phone className="w-3 h-3 text-sky-400" />
                      <span className="text-xs text-gray-400">Total Appels</span>
                    </div>
                    <div className="text-xl font-bold text-white">{stats.calls}</div>
                    <div className="text-xs text-green-400">+12% vs préc.</div>
                  </div>
                  
                  <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-3 h-3 text-green-400" />
                      <span className="text-xs text-gray-400">Complétés</span>
                    </div>
                    <div className="text-xl font-bold text-white">{stats.completed}</div>
                    <div className="text-xs text-green-400">89% taux</div>
                  </div>
                  
                  <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-3 h-3 text-purple-400" />
                      <span className="text-xs text-gray-400">Taux de Succès</span>
                    </div>
                    <div className="text-xl font-bold text-white">89%</div>
                  </div>
                  
                  <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-3 h-3 text-orange-400" />
                      <span className="text-xs text-gray-400">Durée Moy.</span>
                    </div>
                    <div className="text-xl font-bold text-white">{stats.duration}s</div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                  <div className="text-xs font-semibold text-gray-300 mb-2">Appels Récents</div>
                  <div className="space-y-2">
                    {[
                      { name: 'M. Dupont', time: '2 min ago', status: 'completed' },
                      { name: 'Mme Martin', time: '15 min ago', status: 'completed' },
                      { name: 'Dr. Bernard', time: '1h ago', status: 'failed' }
                    ].map((call, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-2">
                          {call.status === 'completed' ? (
                            <CheckCircle className="w-3 h-3 text-green-400" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-400" />
                          )}
                          <span className="text-gray-300">{call.name}</span>
                        </div>
                        <span className="text-gray-500">{call.time}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'agents' && (
              <motion.div
                key="agents"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-sm font-bold text-white mb-4">Agents IA</h3>
                
                {/* Agent Card */}
                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg p-3 border border-purple-500/20 mb-3">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">Restaurant Agent</div>
                      <div className="text-xs text-gray-400">Réservation automatique</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.div 
                      className="w-2 h-2 rounded-full bg-green-400"
                      animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                    <span className="text-xs text-green-400">En ligne</span>
                  </div>
                </div>

                {/* Templates */}
                <div className="text-xs font-semibold text-gray-400 mb-2">Modèles disponibles</div>
                <div className="space-y-2">
                  {['Assistant Juridique', 'Agent Salon Beauté', 'Support E-commerce'].map((template, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-white/5 rounded-lg p-2 border border-white/5 hover:border-purple-500/30 cursor-pointer transition-colors"
                    >
                      <span className="text-xs text-gray-300">{template}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'calls' && (
              <motion.div
                key="calls"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-sm font-bold text-white mb-4">Historique d'appels</h3>
                
                <div className="space-y-2">
                  {[
                    { phone: '+33 6 12 34 56 78', duration: '2m 34s', status: 'completed', agent: 'Restaurant' },
                    { phone: '+33 6 98 76 54 32', duration: '1m 12s', status: 'completed', agent: 'Restaurant' },
                    { phone: '+33 7 11 22 33 44', duration: '0m 45s', status: 'failed', agent: 'Restaurant' }
                  ].map((call, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-white/5 rounded-lg p-3 border border-white/5"
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <div className="text-xs font-semibold text-white">{call.phone}</div>
                          <div className="text-xs text-gray-400">{call.agent}</div>
                        </div>
                        {call.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-gray-500" />
                        <span className="text-xs text-gray-400">{call.duration}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {activeTab === 'billing' && (
              <motion.div
                key="billing"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-sm font-bold text-white mb-4">Facturation & Utilisation</h3>
                
                {/* Current Plan */}
                <div className="bg-gradient-to-br from-sky-500/10 to-purple-500/10 rounded-lg p-3 border border-sky-500/20 mb-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-sm font-bold text-white">Starter Plan</div>
                      <div className="text-xs text-gray-400">120 minutes/mois</div>
                    </div>
                    <div className="text-lg font-bold text-white">79€</div>
                  </div>
                  
                  {/* Usage Bar */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-400">Minutes utilisées</span>
                      <span className="text-white">47 / 120</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-sky-500 to-purple-500"
                        initial={{ width: 0 }}
                        animate={{ width: '39%' }}
                        transition={{ duration: 1, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                  <div className="text-xs font-semibold text-gray-300 mb-2">Plan inclut:</div>
                  <div className="space-y-1.5">
                    {['1 Agent IA', 'Voix premium', 'Dashboard analytics', 'Support email'].map((feature, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle className="w-3 h-3 text-green-400" />
                        <span className="text-xs text-gray-300">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Status bar */}
      <div className="px-4 py-2 bg-gray-800/50 border-t border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <motion.div 
            className="w-2 h-2 rounded-full bg-green-400"
            animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <span className="text-xs text-gray-400">Tous les systèmes opérationnels</span>
        </div>
      </div>
    </div>
  );
};

export default SaaSPreview;
